/* istanbul ignore file */
import {
  Box,
  Button,
  Card,
  DialogV2 as Dialog,
  Flex,
  Heading,
  Loader,
  Text,
  styled,
} from '@kandji-inc/nectar-ui';
import { useQuery } from '@tanstack/react-query';
import React, {
  useContext,
  useEffect,
  useState,
  type SetStateAction,
  type Dispatch,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { rootApi } from 'src/app/api/base';
import { useApolloMeetings } from 'src/config/apollo-meetings';
import { useFlags } from 'src/config/feature-flags';
import useAccount, { AccountContext } from 'src/contexts/account';
import { useSidebarOverlay } from 'src/hooks/useSidebarOverlay';
import { i18n } from 'src/i18n';
import useWhiteBg from '../util/hooks/use-white-bg';
import CheckoutDialog from './CheckoutDialog';
import ContactSalesDialog from './ContactSalesDialog';
import QuoteDialog from './QuoteDialog';
import TrialResources from './Resources';
import { SupportDialog } from './SupportDialog';
import TrialEnded from './TrialEnded';
import Walkthrough from './Walkthrough';
import HandWave from './assets/hand-wave.svg';
import TrialBannerMac from './assets/trial-banner-mac.png';
import TrialCtaBanner from './assets/trial-cta.png';
import {
  convertToCurrencyFormat,
  daysLeftInTrial,
  getTotalTrialDays,
} from './common.data';
import { OnboardingDialog } from './onboarding';
import { useDismissOnboardingDialog } from './useDismissOnboardingDialog';
import { type Quote, useGetQuote } from './useGetQuote';
import { useIsFirstTrialUserSession } from './useIsFirstTrialUserSession';

const FlexLayout = styled(Box, {
  display: 'flex',
  gap: '24px',
  height: '100%',
});

const LeftColumn = styled(Flex, {
  flex: 1,
  flexDirection: 'column',
  gap: '24px',
});

const RightColumn = styled(Flex, {
  width: '33%',
  flexDirection: 'column',
  gap: '24px',
});

const Header = styled(Flex, {
  position: 'relative',
  flexDirection: 'column',
  gap: '12px',
  height: '115px',
  padding: '16px',
  borderRadius: '8px',
  background: 'linear-gradient(91deg, #0F1317 0.18%, #51667B 103.75%)',
});

const ProgressBar = styled(Box, {
  position: 'relative',
  height: '6px',
  maxWidth: '420px',
  borderRadius: '4px',
  background: '#EDEDED',

  '&:after': {
    content: '',
    position: 'absolute',
    left: '0',
    backgroundColor: '$yellow50',
    height: '6px',
    transition: 'all 0.2s ease-in-out',
    borderRadius: '4px',
  },
});

const QuoteExistsView = ({
  quote,
  toggleContactSales,
  toggleQuoteDialog,
}: {
  quote: Quote;
  toggleContactSales: Dispatch<
    SetStateAction<{ isOpen: boolean; error: null }>
  >;
  toggleQuoteDialog: Dispatch<
    SetStateAction<{ isOpen: boolean; openOnDetails: boolean }>
  >;
}) => {
  return (
    <Flex flow="column">
      <Text css={{ fontWeight: 500, lineHeight: '20px' }}>
        {i18n.t('Your quote')}
      </Text>
      <Flex alignItems="center" gap="xs">
        <Heading size="3">
          {convertToCurrencyFormat(
            quote.quoteTotal,
            2,
            'en',
            quote.quoteTotalCurrency,
          )}
        </Heading>
      </Flex>
      <Text size="1">{i18n.t('for annual contract')}</Text>
      <Text
        size="1"
        variant="primary"
        css={{ cursor: 'pointer', fontWeight: 500, marginTop: '4px' }}
        onClick={() => toggleContactSales({ isOpen: true, error: null })}
        data-testid="contact-sales"
      >
        {i18n.t('Contact sales')}
      </Text>
      <Button
        compact
        css={{ position: 'absolute', top: '12px', right: '14px' }}
        data-testid="quote-details-button"
        onClick={() =>
          toggleQuoteDialog((prev) => ({
            ...prev,
            isOpen: true,
            openOnDetails: true,
          }))
        }
      >
        {i18n.t('Details')}
      </Button>
    </Flex>
  );
};

const GetAQuoteView = ({
  LDFF_quoteSyntax,
  toggleQuoteDialog,
  toggleContactSales,
}: {
  LDFF_quoteSyntax: string;
  toggleQuoteDialog: Dispatch<
    SetStateAction<{ isOpen: boolean; openOnDetails: boolean }>
  >;
  toggleContactSales: Dispatch<
    SetStateAction<{ isOpen: boolean; error: null }>
  >;
}) => {
  return (
    <>
      <Text>{i18n.t('Ready to learn more?')}</Text>
      <Button
        id="trial-dashboard-get-a-quote"
        data-testid="get-a-quote-button"
        onClick={() => toggleQuoteDialog((prev) => ({ ...prev, isOpen: true }))}
      >
        {i18n.t(LDFF_quoteSyntax)}
      </Button>
      <Flex alignItems="center" gap="xs">
        <Text>{i18n.t('or')} </Text>
        <Text
          variant="primary"
          css={{ cursor: 'pointer' }}
          data-testid="contact-sales"
          onClick={() => toggleContactSales({ isOpen: true, error: null })}
        >
          {i18n.t('contact sales')}
        </Text>
      </Flex>
    </>
  );
};

export const CheckoutView = ({
  toggleCheckoutDialog,
  toggleSupportDialog,
}: {
  toggleCheckoutDialog: Dispatch<SetStateAction<{ isOpen: boolean }>>;
  toggleSupportDialog: Dispatch<SetStateAction<boolean>>;
}) => {
  return (
    <Flex flow="column" gap="md" css={absolutePositionCenterStyle}>
      <Button
        onClick={() => toggleCheckoutDialog({ isOpen: true })}
        variant="primary"
        data-testid="upgrade-button"
      >
        {i18n.t('Upgrade Now')}
      </Button>
      <Flex justifyContent="center" gap="xs">
        <Text>{i18n.t('or')} </Text>
        <Text
          variant="primary"
          css={{ cursor: 'pointer' }}
          onClick={() => toggleSupportDialog(true)}
          data-testid="contact-sales"
        >
          {i18n.t('Need help?')}
        </Text>
      </Flex>
    </Flex>
  );
};

const ContactSalesView = ({
  toggleContactSales,
  isContactSalesDialogOpen,
}: {
  toggleContactSales: Dispatch<
    SetStateAction<{ isOpen: boolean; error: null }>
  >;
  isContactSalesDialogOpen: boolean;
}) => {
  useSidebarOverlay(isContactSalesDialogOpen, 'contact-sales-dialog');

  return (
    <div style={absolutePositionCenterStyle}>
      <Text size="1">{i18n.t('Ready to learn more?')}</Text>
      <Button
        onClick={() => toggleContactSales({ isOpen: true, error: null })}
        data-testid="contact-sales"
      >
        {i18n.t('Contact sales')}
      </Button>
    </div>
  );
};

const BookDemoView = () => {
  const { 'exp_04212025-product-expert': LDFF_expProductExpert } = useFlags([
    'exp_04212025-product-expert',
  ]);
  const [isProductExpertVideoOpen, setIsProductExpertVideoOpen] =
    useState(false);

  const { userEmail, userFirstName, userLastName, currentCompany } =
    useAccount();

  useSidebarOverlay(isProductExpertVideoOpen, 'product-expert-demo-video');
  const { submit: submitApolloMeeting, isReady } = useApolloMeetings();

  const setMeeting = () => {
    submitApolloMeeting(
      {
        email: userEmail,
        full_name: `${userFirstName} ${userLastName}`,
        company_name: currentCompany?.name as string,
      },
      () => {},
    );
  };

  const toggleProductExpertVideo = () => {
    setIsProductExpertVideoOpen(true);
  };

  return (
    <>
      <Flex flow="column" alignItems="center" css={{ gap: '8px' }}>
        {!currentCompany ? (
          <Loader size="md" css={absolutePositionCenterStyle} />
        ) : (
          <>
            <Button
              variant="primary"
              onClick={setMeeting}
              data-testid="contact-sales"
              disabled={!isReady}
              css={{
                position: 'relative',
                '&:disabled': {
                  opacity: 0.7,
                  cursor: 'not-allowed',
                },
              }}
            >
              {i18n.t('Book a demo')}
            </Button>
            {LDFF_expProductExpert && (
              <Text size="2">
                {i18n.t('Or hear from a ')}
                <Button
                  variant="link"
                  onClick={toggleProductExpertVideo}
                  data-testid="product-expert-demo-video"
                >
                  {i18n.t('product expert')}
                </Button>
              </Text>
            )}
          </>
        )}
      </Flex>
      <Dialog
        isOpen={isProductExpertVideoOpen}
        onOpenChange={(open) => setIsProductExpertVideoOpen(open)}
        closeOnOutsideClick
        closeOnEscape
        title={i18n.t('Product Expert Overview')}
        headerContent={
          <Text size="2" variant="secondary">
            {i18n.t(
              'Explore top use cases with Kandji from one of our product experts',
            )}
          </Text>
        }
        content={
          <Flex
            flow="column"
            gap="4"
            css={{ width: '60vw', height: 'calc(60vw * 9/16)' }}
          >
            <video
              width="auto"
              height="auto"
              autoPlay
              controls
              src={
                'https://resources.kandji.io/hubfs/CTA/product-expert-demo.mp4'
              }
            >
              <track kind="captions" />
            </video>
          </Flex>
        }
      />
    </>
  );
};

const TrialDashboard = () => {
  const { data: hubspotDeal, isLoading } = useQuery({
    queryKey: ['hubspot-deal'],
    queryFn: async () => {
      return await rootApi('trial/v1/eligibility').get();
    },
  });

  const isFirstTrialUserSession = useIsFirstTrialUserSession();
  const { isDismissed, dismissDialog } = useDismissOnboardingDialog();
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useState(false);

  React.useEffect(() => {
    if (isFirstTrialUserSession && !isDismissed) {
      setIsOnboardingDialogOpen(true);
    }
  }, [isFirstTrialUserSession, isDismissed]);

  const history = useHistory();
  const {
    'exp_041625-sales-intervention': LDFF_SalesIntervention,
    'exp-74-trial-dashboard-quoting': LDFF_quotingEnabled,
    'exp-012325-stripe-checkout': isStripeCheckoutOn,
    'dc-exp-quote-syntax': LDFF_quoteSyntax,
    'exp-override-view-checkout': LDFF_OVERRIDE_VIEW_CHECKOUT,
  } = useFlags([
    'exp-74-trial-dashboard-quoting',
    'exp-012325-stripe-checkout',
    'dc-exp-quote-syntax',
    'exp-override-view-checkout',
    'exp_041625-sales-intervention',
  ]);
  const { currentCompany, userFirstName: firstName } =
    useContext(AccountContext);
  useWhiteBg();

  const TOTAL_TRIAL_DAYS = getTotalTrialDays(
    currentCompany?.created_at * 1000,
    +new Date(currentCompany.trial_banner_data?.trial_end_date),
  );
  const trialDaysLeft = daysLeftInTrial(
    currentCompany.trial_banner_data?.trial_end_date,
  );

  const { data: quoteData } = useGetQuote();
  const [isQuoteDialogOpen, toggleQuoteDialog] = useState({
    isOpen: false,
    openOnDetails: false,
  });
  const [isContactSalesDialogOpen, toggleContactSales] = useState({
    isOpen: false,
    error: null,
    openedFromSupportDialog: false,
  });
  const [isSupportDialogOpen, toggleSupportDialog] = useState(false);
  const [isCheckoutDialogOpen, toggleCheckoutDialog] = useState({
    isOpen: false,
  });

  const isLastDayOfTrial = trialDaysLeft === 1;
  const isTrialEnded = !trialDaysLeft;

  useEffect(() => {
    history.push('/trial');
  }, [history]);

  if (isTrialEnded) {
    return <TrialEnded />;
  }

  const isFromSignupFlow = Boolean(
    currentCompany.onboarding?.experience === 'v1',
  );
  const isQuotingEnabled = LDFF_quotingEnabled && isFromSignupFlow;

  const isDemoBookingEnabled = LDFF_SalesIntervention;

  const getCTAContent = () => {
    if (isLoading) {
      return (
        <Loader
          size="md"
          css={{
            ...absolutePositionCenterStyle,
            transform: 'translate(100%, -50%)',
          }}
        />
      );
    }
    if (isQuotingEnabled && quoteData) {
      return (
        <QuoteExistsView
          quote={quoteData}
          toggleContactSales={toggleContactSales}
          toggleQuoteDialog={toggleQuoteDialog}
        />
      );
    }

    if (
      (isStripeCheckoutOn && hubspotDeal?.data?.eligible) ||
      LDFF_OVERRIDE_VIEW_CHECKOUT
    ) {
      return (
        <CheckoutView
          toggleCheckoutDialog={toggleCheckoutDialog}
          toggleSupportDialog={toggleSupportDialog}
        />
      );
    }

    if (isQuotingEnabled) {
      return (
        <GetAQuoteView
          LDFF_quoteSyntax={LDFF_quoteSyntax}
          toggleQuoteDialog={toggleQuoteDialog}
          toggleContactSales={toggleContactSales}
        />
      );
    }

    if (isDemoBookingEnabled) {
      return <BookDemoView />;
    }

    return (
      <ContactSalesView
        toggleContactSales={toggleContactSales}
        isContactSalesDialogOpen={isContactSalesDialogOpen}
      />
    );
  };

  return (
    <>
      <OnboardingDialog
        open={isOnboardingDialogOpen}
        onOpenChange={(open) => {
          setIsOnboardingDialogOpen(open);
          if (!open) {
            dismissDialog();
          }
        }}
      />
      <Box
        p5
        css={{
          height: 'calc(100vh - 48px)',
          minWidth: '1250px',
          marginLeft: '-49px',
          marginRight: '-48px',

          '@media (min-width: 1712px)': {
            maxWidth: '1760px',
            margin: 'auto',
          },
        }}
      >
        <FlexLayout>
          <LeftColumn>
            <Header
              css={{
                backgroundImage: `url(${TrialBannerMac}), linear-gradient(91deg, rgb(15, 19, 23) 0.18%, rgb(81, 102, 123) 103.75%)`,
                borderRadius: '7px',
                backgroundPosition: 'right center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
              }}
            >
              <Box css={{ maxWidth: '50%' }}>
                <Flex flow="column" gap="xs" mb3>
                  <Flex gap="xs" alignItems="center">
                    <ReactSVG src={HandWave} alt="wave" />
                    <Text css={{ color: '$neutral0' }}>
                      {firstName
                        ? isLastDayOfTrial
                          ? i18n.t('Hi {firstName}, today is your', {
                              firstName,
                            })
                          : i18n.t('Hi {firstName},  you have', {
                              firstName,
                            })
                        : ''}

                      {!firstName
                        ? isLastDayOfTrial
                          ? i18n.t('Today is your')
                          : i18n.t('You have')
                        : ''}
                    </Text>
                  </Flex>
                  <Heading
                    size="3"
                    css={{
                      color: '$neutral0',
                      fontWeight: 500,
                      letterSpacing: '-0.8px',
                    }}
                  >
                    {isLastDayOfTrial
                      ? i18n.t('Final day in trial')
                      : i18n.t('{daysLeft} days left in trial', {
                          daysLeft: trialDaysLeft,
                        })}
                  </Heading>
                </Flex>
                <ProgressBar
                  key={trialDaysLeft}
                  css={{
                    '&::after': {
                      width: `calc((100% / ${TOTAL_TRIAL_DAYS}) * ${
                        TOTAL_TRIAL_DAYS - trialDaysLeft
                      } + 2%)`,
                    },
                  }}
                />
              </Box>
            </Header>
            <Walkthrough />
          </LeftColumn>
          <RightColumn>
            <Card
              css={{
                position: 'relative',
                display: 'flex',
                padding: 0,
                minHeight: '225px',
              }}
            >
              <Flex
                flow="column"
                justifyContent="center"
                alignItems="center"
                css={{ width: '100%', height: '100%', padding: 0, gap: '24px' }}
              >
                <Box
                  css={{
                    width: '100%',
                    minHeight: '112px',
                    position: 'relative',
                    backgroundImage: `url(${TrialCtaBanner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                  }}
                >
                  <Text
                    size="4"
                    variant="default"
                    css={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontWeight: 500,
                    }}
                  >
                    {i18n.t("Let's talk solutions")}
                  </Text>
                </Box>
                <Box css={{ width: '100%', height: '100%' }}>
                  {getCTAContent()}
                </Box>
              </Flex>
            </Card>
            <TrialResources />
          </RightColumn>
        </FlexLayout>
        {isCheckoutDialogOpen.isOpen && (
          <CheckoutDialog
            onClose={() => toggleCheckoutDialog({ isOpen: false })}
            onToggleContactSales={({ error }) => {
              toggleCheckoutDialog({ isOpen: false });
              toggleContactSales({ isOpen: true, error });
            }}
          />
        )}
        {isQuoteDialogOpen.isOpen && (
          <QuoteDialog
            openOnDetails={isQuoteDialogOpen.openOnDetails}
            details={quoteData}
            onClose={() =>
              toggleQuoteDialog((prev) => ({ ...prev, isOpen: false }))
            }
            onToggleContactSales={({ error }) => {
              toggleQuoteDialog((prev) => ({ ...prev, isOpen: false }));
              toggleContactSales({ isOpen: true, error });
            }}
          />
        )}
        <ContactSalesDialog
          pageName="Trial Dashboard"
          isOpen={isContactSalesDialogOpen.isOpen}
          setIsOpen={(isOpen) =>
            toggleContactSales({
              isOpen,
              error: null,
              openedFromSupportDialog: false,
            })
          }
          withError={isContactSalesDialogOpen.error}
          openedFromSupportDialog={
            isContactSalesDialogOpen.openedFromSupportDialog
          }
          goBackToSupportDialog={() => {
            toggleContactSales({
              isOpen: false,
              error: null,
              openedFromSupportDialog: false,
            });
            toggleSupportDialog(true);
          }}
        />

        <SupportDialog
          isOpen={isSupportDialogOpen}
          setIsOpen={(isOpen) => toggleSupportDialog(isOpen)}
          toggleContactSales={toggleContactSales}
        />
      </Box>
    </>
  );
};

const absolutePositionCenterStyle = {
  position: 'absolute',
  left: '45%',
  top: '75%',
  transform: 'translate(-15%, -50%)',
};

export default TrialDashboard;
